import React from 'react';
import { emitLeadChanged, emitMeetingChanged } from '../lib/events';
import { useAuth } from '../context/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Skeleton } from './ui/skeleton';
import { LeadDrawer } from './LeadDrawer';
import { MeetingSidePanel } from './meetings/MeetingSidePanel';
import { OutcomeDialog } from './meetings/OutcomeDialog';
import { MeetingFormDialog } from './meetings/MeetingFormDialog';
import { PanelError } from './summary/SummaryShared';
import { LeadSummary } from './summary/LeadSummary';
import { QuotationSummary } from './summary/QuotationSummary';
import { StaffSummary } from './summary/StaffPanel';
import { useSummaryPanel } from './summary/useSummaryPanel';
import { Building2, User, Users, FileText, CalendarDays } from 'lucide-react';

const SummaryBody = ({ data, target, canManageStaff, staffOptions, onEditLead, onStaffSaved, onOpenDirectory, onQuotationAction }) => {
  if (!data) {
    return (
      <div className="space-y-3 mt-2" data-testid="summary-loading">
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}
      </div>
    );
  }
  if (data.error) return <PanelError notFound={data.error === 'not_found'} />;
  if (target?.type === 'lead') return <LeadSummary data={data} onEdit={onEditLead} />;
  if (target?.type === 'quotation') {
    return <QuotationSummary data={data}
      onOpenFull={() => onQuotationAction('open')}
      onDownload={() => onQuotationAction('download')}
      onSend={() => onQuotationAction('send')}
      onWhatsApp={() => onQuotationAction('whatsapp')} />;
  }
  if (target?.type === 'meeting') return null; // meetings render in their own side panel once loaded
  return <StaffSummary data={data} canManage={canManageStaff} options={staffOptions}
    onSaved={onStaffSaved} onOpenDirectory={onOpenDirectory} />;
};

const PANEL_TITLES = {
  lead: <><Building2 size={14} /> Lead Summary</>,
  'lead-loading': <><Building2 size={14} /> Opening Lead…</>,
  meeting: <><CalendarDays size={14} /> Opening Meeting…</>,
  quotation: <><FileText size={14} /> Quotation Summary</>,
  staff: <><Users size={14} /> Staff Profile</>,
};

const PanelTitle = ({ target }) => (
  <SheetTitle className="font-heading text-sm text-muted-foreground flex items-center gap-1.5">
    {PANEL_TITLES[target?.type] || <><User size={14} /> Summary</>}
  </SheetTitle>
);

/** Dialogs mounted alongside the panel: the full lead editor and the global
 * meeting side panel + outcome/edit dialogs (usable from any page). */
const PanelDialogs = ({ p }) => (
  <>
    <LeadDrawer lead={p.editLead} members={p.members} open={!!p.editLead} onClose={() => p.setEditLead(null)}
      onSaved={(u) => { emitLeadChanged(u); }}
      onDeleted={() => { p.setEditLead(null); emitLeadChanged(); }} />

    <MeetingSidePanel meeting={p.meeting} open={!!p.meeting} onClose={() => p.setMeeting(null)}
      onChanged={p.onChanged}
      onOutcome={() => p.setOutcomeFor(p.meeting)}
      onEdit={() => { p.setEditMeeting(p.meeting); p.setMeeting(null); }}
      canDelete={p.meetingMeta?.can_delete} canOverride={p.meetingMeta?.can_override_conflicts} />
    <OutcomeDialog open={!!p.outcomeFor} onClose={() => p.setOutcomeFor(null)} meeting={p.outcomeFor}
      onSaved={(m) => { p.setMeeting(m); emitMeetingChanged(m); emitLeadChanged(); }} />
    <MeetingFormDialog open={!!p.editMeeting} onClose={() => p.setEditMeeting(null)} meeting={p.editMeeting}
      onSaved={() => {
        const id = p.editMeeting?.id;
        p.setEditMeeting(null);
        emitMeetingChanged();
        if (id) p.reopen(id);
      }} />
  </>
);

/** Global side panel opened from any search bar (Global / Staff / Kanban).
 * Leads: opens the full Edit Lead drawer (Search-to-Action).
 * Staff: opens a directly editable profile panel (RBAC-gated).
 * Meetings: opens the full Meeting side panel with outcome/edit/reschedule actions.
 * All panels open OVER the current page \u2014 never a navigation or blank route. */
export const EntitySummaryPanel = () => {
  const p = useSummaryPanel();
  const { hasPerm } = useAuth();

  return (
    <>
      <Sheet open={!!p.target} onOpenChange={(o) => !o && p.setTarget(null)}>
        <SheetContent className="w-full sm:max-w-[420px] overflow-y-auto" data-testid="entity-summary-panel">
          <SheetHeader className="pb-2">
            <PanelTitle target={p.target} />
          </SheetHeader>
          <SummaryBody data={p.data} target={p.target}
            canManageStaff={hasPerm('team', 'manage')} staffOptions={p.staffOptions}
            onEditLead={p.openLeadEditor} onStaffSaved={p.onStaffSaved}
            onOpenDirectory={p.openStaffDirectory} onQuotationAction={p.quotationAction} />
        </SheetContent>
      </Sheet>
      <PanelDialogs p={p} />
    </>
  );
};
